<template>
    <div class="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div class="max-w-md w-full space-y-8">
            <div v-if="step === 0">
                <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Bienvenue sur Termoflow !
                </h2>
                <p class="mt-2 text-center text-sm text-gray-600">
                    Terminer votre configuration pour commencer à utiliser la plateforme.
                </p>
                <div class="text-center">
                    <button @click="step = 1" class="px-4 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition duration-200 mt-6">
                        C'est parti <i class="fas fa-arrow-right ml-1"></i>
                    </button>

                    <div class="mt-8 text-xs font-light">
                        <button @click="logout">Se déconnecter</button>
                    </div>
                </div>
            </div>
            <div v-if="step === 1">
                <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    A propos de votre société
                </h2>
                <p class="mt-2 mb-4 text-center text-sm text-gray-600">
                    Ajoutez des informations sur votre société pour personnaliser vos documents et gérer votre planning.
                </p>

                <!-- Bloc : Adresse -->
                <div class="bg-white rounded-xl shadow p-6 mb-6">
                    <h2 class="text-lg font-medium text-gray-700 mb-0.5">Adresse</h2>
                    <p class="text-sm text-gray-600 mb-4">Utilisé pour les documents et la facturation</p>

                    <adresse-form v-model="societe.adresse" :min="true" />
                </div>

                <!-- Bloc : Horaires -->
                <div class="bg-white rounded-xl shadow p-6 mb-6">
                    <h2 class="text-lg font-medium text-gray-700 mb-0.5">Horaires d’ouverture</h2>
                    <p class="text-sm text-gray-600 mb-4">Utilisé pour le planning</p>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1">Heure d’ouverture</label>
                            <input v-model="societe.heure_ouverture" type="time" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" id="inp-heure_ouverture" />
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1">Heure de fermeture</label>
                            <input v-model="societe.heure_fermeture" type="time" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" id="inp-heure_fermeture" />
                        </div>

                    </div>
                </div>

                <div class="text-center flex justify-center gap-2">
                    <button @click="step = 0" class="px-4 bg-slate-200 hover:bg-slate-300 font-semibold py-3 rounded-lg transition duration-200 mt-6">
                        <i class="fas fa-arrow-left mr-1"></i> Retour
                    </button>
                    <button @click="nextStep(2)" class="px-4 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition duration-200 mt-6">
                        Continuer <i class="fas fa-arrow-right ml-1"></i>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="step === 2" class="max-w-2xl w-full space-y-8">
            <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
                Concernant votre activité
            </h2>
            <p class="mt-6 text-center font-bold mx-auto text-2xl">
                Sélectionnez les types d’équipements que vous installez ou dépannez :
            </p>
            <div class="flex flex-col w-[400px] mx-auto p-6 rounded-lg space-y-3">
                <div v-for="activite in activites" :key="activite.name">
                    <div class="flex items-center justify-left">
                        <input type="checkbox" :id="activite.name" v-model="selectedActivites[activite.name]" class="h-6 w-6 mr-2 text-red-600 border-gray-300 rounded focus:ring-red-500" />
                        <label :for="activite.name" class="ml-2 block text-gray-700" :class="{ 'font-bold': selectedActivites[activite.name], 'font-thin': !selectedActivites[activite.name] }">
                            {{ activite.libelle }}
                        </label>
                    </div>
                </div>
            </div>
            <div class="text-center">
                <div class="text-center flex justify-center gap-2">
                    <button @click="step = 1" class="px-4 bg-slate-200 hover:bg-slate-300 font-semibold py-3 rounded-lg transition duration-200 mt-6">
                        <i class="fas fa-arrow-left mr-1"></i> Retour
                    </button>
                    <button @click="nextStep(3)" class="px-4 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition duration-200 mt-6">
                        Continuer <i class="fas fa-arrow-right ml-1"></i>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="step === 3" class="max-w-2xl w-full space-y-8">
            <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
                Valider la configuration
            </h2>
            <p class="mt-2 mb-4 text-center text-sm text-gray-600">
                Vous êtes prêt à utiliser Termoflow ! Cliquez sur le bouton ci-dessous pour finaliser la configuration.
            </p>
            <div class="text-center">
                <button :disabled="loadingSendConfig" @click="sendConfig" class="px-4 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition duration-200 mt-6">
                    <template v-if="loadingSendConfig">
                        <i class="fas fa-spinner fa-spin mr-1"></i> Finalisation en cours...
                    </template>
                    <template v-else>
                        <i class="fas fa-check mr-1"></i> Finaliser la configuration
                    </template>
                </button>
            </div>
        </div>

    </div>
</template>

<script>
import AdresseForm from './../Client/Components/AdresseForm.vue';

export default {
    components: {
        AdresseForm,
    },
    data() {
        return {
            step: 0,
            societe: {
                adresse: {},
                heure_ouverture: '08:00',
                heure_fermeture: '18:00',
            },
            selectedActivites: {
                bruleurs: true,
                chaudieres: true,
                chauffeBains: true,
                climExterieurs: true,
                climInterieurs: true,
                pacExterieurs: true,
                pacInterieurs: true,
                regulations: true,
                thermodynamiques: true,
            },

            activites: [
                { name: 'bruleurs', libelle: 'Brûleurs' },
                { name: 'chaudieres', libelle: 'Chaudières' },
                { name: 'chauffeBains', libelle: 'Chauffe-bains' },
                { name: 'climExterieurs', libelle: 'Clim - Unités extérieures' },
                { name: 'climInterieurs', libelle: 'Clim - Unités intérieures' },
                { name: 'pacExterieurs', libelle: 'PAC - Unités extérieures' },
                { name: 'pacInterieurs', libelle: 'PAC - Unités intérieures' },
                { name: 'regulations', libelle: 'Régulations' },
                { name: 'thermodynamiques', libelle: 'Chauffe-eau thermodynamique' },
            ],

            loadingSendConfig: false,
        }
    },
    computed: {
        selectedActivitesNames() {
            return Object.keys(this.selectedActivites).filter(key => this.selectedActivites[key]);
        }
    },
    methods: {
        nextStep(step) {
            if (step == 2) {
                const validation = this.$inpError([
                    'inp-adresse1:required',
                    'inp-heure_ouverture:required',
                    'inp-heure_fermeture:required',
                ]);

                if (!validation) {
                    this.$toast.error('Formulaire invalide')
                    return
                }
            }

            if (step == 3) {
                if (this.selectedActivitesNames.length === 0) {
                    this.$toast.error('Veuillez sélectionner au moins une activité')
                    return
                }
            }

            this.step = step
        },

        async sendConfig() {
            this.loadingSendConfig = true;

            const payload = {
                societe_adresse1: this.societe.adresse.adresse1,
                societe_adresse2: this.societe.adresse.adresse2,
                societe_code_postal: this.societe.adresse.code_postal,
                societe_ville: this.societe.adresse.ville,
                societe_pays: this.societe.adresse.pays,
                societe_latitude: this.societe.adresse.latitude,
                societe_longitude: this.societe.adresse.longitude,
                societe_heure_ouverture: this.societe.heure_ouverture,
                societe_heure_fermeture: this.societe.heure_fermeture,
                activites: JSON.stringify(this.selectedActivitesNames),
            };

            const response = await this.$request.post('/welcome/complete', payload);

            this.loadingSendConfig = false;

            if (response.error) {
                this.$toast.error(response.error);
            } else {
                this.$router.push({ name: 'Home' });
            }
        },

        logout() {
            if (confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
                localStorage.removeItem('auth-token');
                window.location.href = '/login';
            }
        }
    },
}
</script>

<style lang="scss" scoped>

</style>